import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Typography from "@mui/material/Typography";
import Avatar from "@mui/material/Avatar";
import Icon from "@mui/material/Icon";
import CircularProgress from "@mui/material/CircularProgress";
import FormControl from "@mui/material/FormControl";
import InputAdornment from "@mui/material/InputAdornment";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import AddIcon from "@mui/icons-material/Add";
import PrintIcon from "@mui/icons-material/PrintOutlined";

import PageHeader from "components/PageHeader";
import DataTable from "components/DataTable";
import ConfirmDialog from "components/ConfirmDialog";
import FormDialog from "components/FormDialog";
import FormSection from "components/FormSection";
import { useAuth } from "contexts/AuthContext";

import useProjectData from "layouts/tables/data/projectTable";
import CampaignReportPanel from "./CampaignReportPanel";

function Projects() {
  const { user } = useAuth();
  const isChurchAdmin = ["admin", "super_admin", "pastor", "treasurer", "branch_admin"].includes(
    user?.role
  );
  const isGroupHead = ["department_head", "ministry_head"].includes(user?.role);
  const {
    columns,
    rows,
    isLoading,
    error,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedProject,
    setSelectedProject,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddProject,
    contributeModalOpen,
    setContributeModalOpen,
    contributingProject,
    contribution,
    setContribution,
    searchTerm,
    setSearchTerm,
    filteredMembers,
    isLoadingMembers,
    showMemberDropdown,
    setShowMemberDropdown,
    handleMemberSelect,
    handleContributionSubmit,
    isSavingContribution,
    isGroupContribution,
    setIsGroupContribution,
    contributorName,
    setContributorName,
  } = useProjectData();

  if (isLoading) {
    return <Typography sx={{ py: 6 }}>Loading projects...</Typography>;
  }

  if (error) {
    return (
      <Typography color="error" sx={{ py: 6 }}>
        Error: {error}
      </Typography>
    );
  }

  return (
    <Box>
      <PageHeader
        title={isGroupHead ? "Our Contribution Campaigns" : "Projects"}
        subtitle={
          isGroupHead
            ? "Raise a contribution campaign for your own department/ministry — members of your group will see it on their dashboard."
            : "Track fundraising projects and the contributions members make toward them, including campaigns raised by departments and ministries."
        }
        actions={
          <>
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleAddProject}>
              {isGroupHead ? "New Campaign" : "New Project"}
            </Button>
            <IconButton onClick={handlePrint} sx={{ border: 1, borderColor: "divider" }}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </>
        }
      />

      {isChurchAdmin && (
        <Box mb={3}>
          <CampaignReportPanel />
        </Box>
      )}

      <Card>
        <DataTable
          table={{ columns, rows }}
          isSorted
          entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
          showTotalEntries
          canSearch
          searchLabel="Search projects..."
        />
      </Card>

      {/* Create / Edit project */}
      <FormDialog
        open={editModalOpen}
        onClose={() => setEditModalOpen(false)}
        title={selectedProject?.id ? "Edit Project" : "New Project"}
        onSave={handleEditSubmit}
        saveLabel={selectedProject?.id ? "Save Changes" : "Create Project"}
        maxWidth="sm"
      >
        <FormSection>
          <TextField
            label="Project Name"
            value={selectedProject?.name || ""}
            onChange={(e) => setSelectedProject({ ...selectedProject, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
          <TextField
            label="Description"
            value={selectedProject?.description || ""}
            onChange={(e) =>
              setSelectedProject({ ...selectedProject, description: e.target.value })
            }
            fullWidth
            margin="normal"
            multiline
            minRows={2}
          />
          <TextField
            label="Goal Amount"
            type="number"
            value={selectedProject?.goalAmount || ""}
            onChange={(e) => setSelectedProject({ ...selectedProject, goalAmount: e.target.value })}
            fullWidth
            margin="normal"
            InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }}
          />
          <TextField
            label="Start Date"
            type="date"
            value={selectedProject?.startDate || ""}
            onChange={(e) => setSelectedProject({ ...selectedProject, startDate: e.target.value })}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="End Date"
            type="date"
            value={selectedProject?.endDate || ""}
            onChange={(e) => setSelectedProject({ ...selectedProject, endDate: e.target.value })}
            fullWidth
            margin="normal"
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Status"
            value={selectedProject?.status || "active"}
            onChange={(e) => setSelectedProject({ ...selectedProject, status: e.target.value })}
            fullWidth
            margin="normal"
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="completed">Completed</MenuItem>
            <MenuItem value="inactive">Inactive</MenuItem>
          </TextField>
        </FormSection>
      </FormDialog>

      {/* Record a contribution */}
      <FormDialog
        open={contributeModalOpen}
        onClose={() => setContributeModalOpen(false)}
        title={`Record Contribution — ${contributingProject?.name || ""}`}
        onSave={handleContributionSubmit}
        saveLabel={isSavingContribution ? "Saving..." : "Record Contribution"}
        saveDisabled={
          (isGroupContribution ? !contributorName.trim() : !contribution.member_id) ||
          !contribution.amount ||
          isSavingContribution
        }
        loading={isSavingContribution}
        maxWidth="sm"
      >
        <FormSection>
          <FormControlLabel
            control={
              <Switch
                checked={isGroupContribution}
                onChange={(e) => setIsGroupContribution(e.target.checked)}
              />
            }
            label="Group / collective contribution (no specific member)"
            sx={{ mb: 1 }}
          />

          {isGroupContribution ? (
            <TextField
              label="Description"
              placeholder="e.g. Sunday service collection"
              value={contributorName}
              onChange={(e) => setContributorName(e.target.value)}
              fullWidth
              margin="normal"
              required
              helperText="Who or what gathering this lump-sum contribution came from."
            />
          ) : (
            <FormControl fullWidth>
              <TextField
                label="Search Member"
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setShowMemberDropdown(e.target.value.trim() !== "");
                }}
                onFocus={() => {
                  if (searchTerm.trim() !== "" && filteredMembers.length > 0) {
                    setShowMemberDropdown(true);
                  }
                }}
                onBlur={() => setTimeout(() => setShowMemberDropdown(false), 200)}
                fullWidth
                margin="normal"
                required
                InputProps={{
                  endAdornment: isLoadingMembers && <CircularProgress color="inherit" size={20} />,
                }}
              />
              {showMemberDropdown && (
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1300,
                    width: "100%",
                    maxHeight: 250,
                    overflowY: "auto",
                    bgcolor: "background.paper",
                    boxShadow: 3,
                    border: 1,
                    borderColor: "divider",
                    borderRadius: 1,
                    mt: 0.5,
                  }}
                >
                  {filteredMembers.length > 0 ? (
                    filteredMembers.map((member) => (
                      <MenuItem
                        key={member.id}
                        onClick={() => handleMemberSelect(member)}
                        sx={{ py: 1, px: 2, display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Avatar sx={{ width: 36, height: 36 }}>
                          <Icon>person</Icon>
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={700}>
                            {member.first_name} {member.surname}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            ID: {member.memberID}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled sx={{ py: 1, px: 2 }}>
                      {isLoadingMembers ? "Loading..." : "No members found"}
                    </MenuItem>
                  )}
                </Box>
              )}
            </FormControl>
          )}

          <TextField
            label="Amount"
            type="number"
            value={contribution.amount}
            onChange={(e) => setContribution({ ...contribution, amount: e.target.value })}
            fullWidth
            margin="normal"
            required
            InputProps={{ startAdornment: <InputAdornment position="start">GHS</InputAdornment> }}
          />
        </FormSection>
      </FormDialog>

      <ConfirmDialog
        open={deleteModalOpen}
        onClose={() => setDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        title="Confirm Delete"
        message={`Are you sure you want to delete "${selectedProject?.name}"? Recorded contributions will remain in the payments history.`}
        confirmLabel="Delete"
      />
    </Box>
  );
}

export default Projects;
